// ADR 23.9.26v §3 — the offline key-value store: database `lingara-offline-v1`,
// one object store, `kv`. Hand-written in the tutor store's shape
// (`features/tutor/sessions/store.ts`) rather than `idb-keyval`: three
// operations, every one guarded, so a refused or blocked IndexedDB makes each
// one a no-op and records `offline_cache_storage_failed` instead of throwing.
//
// One database per concern (§1): the tutor's `lingara-tutor-v1` and W3's queue
// have their own, so no `onupgradeneeded` here can break theirs.

import { offlineCacheStorageFailed, type OfflineStorageOperation } from './telemetry'

const DB_NAME = 'lingara-offline-v1'
const DB_VERSION = 1
const KV = 'kv'

export interface OfflineKv {
  get<T>(key: string): Promise<T | undefined>
  set(key: string, value: unknown): Promise<void>
  del(key: string): Promise<void>
}

type FailureSink = (operation: OfflineStorageOperation) => void

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB aborted'))
  })
}

/** Opens the database, reading `window.indexedDB` at call time (absent in jsdom). */
function openOfflineDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(KV)
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
      request.onblocked = () => reject(new Error('IndexedDB open blocked'))
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * The guarded store. A failed `open` is recorded as `open`, a failed
 * operation under its own name, and neither ever reaches the caller.
 */
export function createOfflineDb(
  open: () => Promise<IDBDatabase> = openOfflineDatabase,
  failure: FailureSink = offlineCacheStorageFailed,
): OfflineKv {
  async function safely<T>(
    name: OfflineStorageOperation,
    fallback: T,
    operation: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> {
    let db: IDBDatabase
    try {
      db = await open()
    } catch {
      failure('open')
      return fallback
    }
    try {
      const tx = db.transaction(KV, name === 'get' ? 'readonly' : 'readwrite')
      const result = await operation(tx.objectStore(KV))
      await transactionDone(tx)
      return result
    } catch {
      failure(name)
      return fallback
    } finally {
      db.close()
    }
  }

  return {
    get: <T>(key: string) =>
      safely<T | undefined>('get', undefined, (store) => requestResult(store.get(key)) as Promise<T | undefined>),
    set: (key, value) => safely('set', undefined, async (store) => void (await requestResult(store.put(value, key)))),
    del: (key) => safely('del', undefined, async (store) => void (await requestResult(store.delete(key)))),
  }
}

/** The app's one instance. */
export const offlineDb: OfflineKv = createOfflineDb()
